/**
 * Karmagick — la scritta che scorre dentro la barra di ricerca della testata.
 *
 * Scrive e cancella una frase alla volta, come se qualcuno stesse digitando.
 * È solo decorazione: il bottone ha già il suo aria-label e la scritta è
 * aria-hidden, quindi chi usa uno screen reader non sente nulla di tutto questo.
 *
 * - Parte dalla frase che il Liquid ha già scritto nel campo (niente campo vuoto
 *   in attesa dello script) e dopo due giri si posa sulla prima frase: un testo
 *   che si muove per sempre stanca, e le linee guida chiedono che si fermi.
 * - La avvia e la ferma l'IntersectionObserver: un'etichetta nascosta (telefono,
 *   testata scorsa via) non ha nessun timer acceso.
 * - Si ferma con «riduci movimento», a scheda nascosta e mentre il modale di
 *   ricerca è aperto (due campi che scrivono insieme erano un pasticcio).
 * - Al clic misura il campo e lo passa al CSS (--kg-search-*): il modale si apre
 *   esattamente sopra il campo, come se fosse il campo stesso ad aprirsi.
 */

/* Le frasi arrivano dal Liquid già nella lingua della pagina: data-kg-frasi, separate da «|»
   (kg.search.typing nei file di lingua). La prima è sempre quella già scritta nel campo, così
   la scritta parte da lì e lì torna a posarsi, qualunque cosa abbia scelto chi ha tradotto. */
function frasiDi(nodo) {
  const inPagina = nodo.textContent.trim();
  const tradotte = (nodo.dataset.kgFrasi || '')
    .split('|')
    .map((frase) => frase.trim())
    .filter((frase) => frase && frase !== inPagina);
  return [inPagina, ...tradotte].filter(Boolean);
}

const SCRIVI = 55; // ms per lettera
const CANCELLA = 28;
const PAUSA_PIENA = 2200;
const PAUSA_VUOTA = 400;
const GIRI = 2;

const menoMovimento = window.matchMedia('(prefers-reduced-motion: reduce)');

/* Al clic misura il campo e lo passa al CSS. Solo quando il bottone è davvero vestito da campo
   (barra al centro, da 990 px in su): l'icona da 44 px di tablet e telefono non è un campo, e
   passarne la larghezza apriva il modale largo 44 px. In quel caso le variabili si tolgono e vale
   il ripiego del CSS (centrato, 600 px). */
function misuraAlClic(nodo, bottone) {
  const misura = () => {
    const radice = document.documentElement.style;
    const r = bottone.getBoundingClientRect();
    const campo = nodo.offsetParent !== null && r.width >= 240;
    for (const [chiave, valore] of [['top', r.top], ['left', r.left], ['width', r.width]]) {
      if (campo) radice.setProperty(`--kg-search-${chiave}`, `${Math.round(valore)}px`);
      else radice.removeProperty(`--kg-search-${chiave}`);
    }
  };
  bottone.addEventListener('pointerdown', misura);
  bottone.addEventListener('keydown', misura);
}

function avvia(nodo) {
  const bottone = nodo.closest('button');
  if (bottone) misuraAlClic(nodo, bottone); // vale anche con «riduci movimento»

  // Con «riduci movimento» resta la frase già in pagina; con una frase sola non c'è niente da alternare.
  const FRASI = frasiDi(nodo);
  if (menoMovimento.matches || FRASI.length < 2) return;

  // Si parte dalla frase già in pagina (la prima), in cancellazione.
  let frase = 0;
  let lettere = FRASI[frase].length;
  let cancellando = true;
  let scritte = 0;
  let timer = null;
  let visibile = false;
  let finita = false;

  const ferma = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };

  const passo = () => {
    timer = null;
    if (finita || !visibile || document.hidden || document.querySelector('dialog.search-modal__content[open]')) return;

    const testo = FRASI[frase];
    lettere += cancellando ? -1 : 1;
    nodo.textContent = testo.slice(0, lettere);

    let attesa = cancellando ? CANCELLA : SCRIVI;
    if (!cancellando && lettere === testo.length) {
      scritte++;
      if (scritte >= FRASI.length * GIRI && frase === 0) {
        finita = true; // posata sulla prima frase
        return;
      }
      cancellando = true;
      attesa = PAUSA_PIENA;
    } else if (cancellando && lettere === 0) {
      cancellando = false;
      frase = (frase + 1) % FRASI.length;
      attesa = PAUSA_VUOTA;
    }
    timer = setTimeout(passo, attesa);
  };

  const riparti = (ritardo = PAUSA_PIENA) => {
    if (!timer && !finita) timer = setTimeout(passo, ritardo);
  };

  if ('IntersectionObserver' in window) {
    new IntersectionObserver((voci) => {
      visibile = voci.some((v) => v.isIntersecting);
      if (visibile) riparti();
      else ferma();
    }).observe(nodo);
  } else {
    visibile = true;
    riparti();
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) ferma();
    else if (visibile) riparti(600);
  });

  if (bottone) {
    bottone.addEventListener('click', ferma);
    // Alla chiusura del modale il focus torna sul bottone: la scritta riprende.
    bottone.addEventListener('focus', () => riparti(1200));
  }
}

for (const nodo of document.querySelectorAll('[data-kg-searchbar-label]')) {
  if (nodo.dataset.kgAvviata) continue;
  nodo.dataset.kgAvviata = '1';
  avvia(nodo);
}
